import * as THREE from "three";
import { schematicOrigin, type PlanObject, type PlanStatus, type SchematicRef } from "@mcp/plan";
import type { MeshBuffers } from "@mcp/mesher";
import type { Engine } from "../engine/Engine.ts";
import { createStructureMaterials, type StructureMaterials } from "../engine/materials.ts";

/** planned buildings are see-through and lightly tinted with their colour; "done" looks like real blocks */
const OPACITY: Record<PlanStatus, number> = { idea: 0.45, planned: 0.72, in_progress: 0.86, done: 1 };
const TINT: Record<PlanStatus, number> = { idea: 0.3, planned: 0.16, in_progress: 0.08, done: 0 };

interface Tile { x: number; z: number; solid: THREE.BufferGeometry | null; translucent: THREE.BufferGeometry | null }

/** meshes of one schematic at one slice height, shared by every copy placed in the world */
interface MeshSet {
  key: string;
  tiles: Tile[] | null;
  users: number;
  promise: Promise<Tile[] | null>;
}

interface Placed {
  group: THREE.Group;
  inner: THREE.Group;
  set: MeshSet;
  mats: StructureMaterials;
  sig: string;
  gone: boolean;
}

/**
 * Draws placed schematic buildings with real block models (meshed by the world workers), and the
 * ghost that follows the mouse while placing one. Only for files that are on this computer.
 */
export class StructureRenderer {
  private root: THREE.Group;
  private engine: Engine;
  private getFile: (hash: string) => Promise<{ blob: Blob; name: string } | null>;
  private sets = new Map<string, MeshSet>();
  private placed = new Map<string, Placed>();
  private ghost: Placed | null = null;
  private lastObjs: PlanObject[] = [];
  private busy = 0;
  /** hashes of schematic files in this browser */
  private available = new Set<string>();
  /** world slice height (Slice Y), null = off */
  private cutY: number | null = null;
  private cutTimer = 0;
  dimension = "minecraft:overworld";
  onBusy: (busy: boolean) => void = () => {};
  onError: (msg: string) => void = () => {};

  constructor(engine: Engine, getFile: (hash: string) => Promise<{ blob: Blob; name: string } | null>) {
    this.engine = engine;
    this.root = engine.structRoot;
    this.getFile = getFile;
  }

  has(hash: string) {
    return this.available.has(hash);
  }

  setAvailable(hashes: Iterable<string>) {
    this.available = new Set(hashes);
    this.sync(this.lastObjs);
  }

  /** Slice Y changed: re-mesh (debounced — the slider fires many times while dragging). */
  setCut(cutY: number | null) {
    if (cutY === this.cutY) return;
    this.cutY = cutY;
    clearTimeout(this.cutTimer);
    this.cutTimer = window.setTimeout(() => this.sync(this.lastObjs), 160);
  }

  /** structure-local slice for a building at base y: null = not cut, -1 = cut away completely */
  private relCut(y: number, h: number): number | null {
    if (this.cutY === null) return null;
    const r = this.cutY - y;
    if (r >= h - 1) return null;
    return r < 0 ? -1 : r;
  }

  sync(objs: PlanObject[]) {
    this.lastObjs = objs;
    const seen = new Set<string>();
    for (const o of objs) {
      const ref = o.schematic;
      if (o.kind !== "building" || !ref || o.dimension !== this.dimension || !this.available.has(ref.hash)) continue;
      const cut = this.relCut(o.y, ref.size[1]);
      if (cut === -1) continue;
      seen.add(o.id);
      const [x0, z0] = schematicOrigin(o.points);
      const sig = JSON.stringify([ref.hash, cut, o.status, o.color]);
      const cur = this.placed.get(o.id);
      if (cur && cur.sig === sig) { orient(cur, ref, x0, o.y, z0); continue; }
      // take the new one before letting go of the old, so a shared mesh isn't thrown away and rebuilt
      const p = this.place(ref, cut, OPACITY[o.status], new THREE.Color(o.color), TINT[o.status], sig);
      orient(p, ref, x0, o.y, z0);
      if (cur) this.drop(cur);
      this.placed.set(o.id, p);
    }
    for (const [id, p] of this.placed) if (!seen.has(id)) { this.drop(p); this.placed.delete(id); }
  }

  /** Ghost while placing (null = hide). */
  setGhost(g: { ref: SchematicRef; x0: number; y: number; z0: number } | null) {
    if (!g || !this.available.has(g.ref.hash)) {
      if (this.ghost) { this.drop(this.ghost); this.ghost = null; }
      return;
    }
    if (this.ghost && !this.ghost.set.key.startsWith(g.ref.hash)) { this.drop(this.ghost); this.ghost = null; }
    this.ghost ??= this.place(g.ref, null, 0.62, new THREE.Color(0xffffff), 0.12, "ghost");
    orient(this.ghost, g.ref, g.x0, g.y, g.z0);
  }

  clear() {
    this.sync([]);
    this.setGhost(null);
  }

  // ---------------- internals ----------------

  private place(ref: SchematicRef, cut: number | null, opacity: number, tint: THREE.Color, tintMix: number, sig: string): Placed {
    const set = this.acquire(ref.hash, cut);
    const group = new THREE.Group();
    const inner = new THREE.Group();
    group.add(inner);
    const mats = createStructureMaterials(this.engine.shared, tint, tintMix, opacity);
    const p: Placed = { group, inner, set, mats, sig, gone: false };
    this.root.add(group);
    const fill = (tiles: Tile[] | null) => {
      if (!tiles || p.gone) return;
      for (const t of tiles) {
        const add = (geo: THREE.BufferGeometry, mat: THREE.Material, order: number) => {
          const m = new THREE.Mesh(geo, mat);
          m.position.set(t.x, 0, t.z);
          m.renderOrder = order;
          inner.add(m);
        };
        if (t.solid) {
          if (opacity < 0.999) add(t.solid, mats.depth, 2);
          add(t.solid, mats.solid, 3);
        }
        if (t.translucent) add(t.translucent, mats.translucent, 4);
      }
    };
    if (set.tiles) fill(set.tiles);
    else void set.promise.then(fill);
    return p;
  }

  private drop(p: Placed) {
    p.gone = true;
    p.group.removeFromParent();
    p.mats.depth.dispose(); p.mats.solid.dispose(); p.mats.translucent.dispose();
    this.release(p.set);
  }

  private acquire(hash: string, cut: number | null): MeshSet {
    const key = `${hash}|${cut ?? "all"}`;
    let s = this.sets.get(key);
    if (!s) {
      const set: MeshSet = { key, tiles: null, users: 0, promise: Promise.resolve(null) };
      set.promise = this.load(hash, cut)
        .then((tiles) => {
          // nobody wants it any more (moved/removed while it was being built)
          if (this.sets.get(key) !== set) { disposeTiles(tiles); return null; }
          set.tiles = tiles;
          return tiles;
        })
        .catch((e: Error) => {
          this.onError(`Schematic preview failed: ${e.message}`);
          if (this.sets.get(key) === set) this.sets.delete(key); // try again next time
          return null;
        });
      this.sets.set(key, set);
      s = set;
    }
    s.users++;
    return s;
  }

  private release(s: MeshSet) {
    s.users--;
    if (s.users > 0) return;
    if (this.sets.get(s.key) === s) this.sets.delete(s.key);
    if (s.tiles) { disposeTiles(s.tiles); s.tiles = null; }
  }

  private async load(hash: string, cut: number | null): Promise<Tile[]> {
    const f = await this.getFile(hash);
    if (!f) throw new Error("the file isn't on this computer any more");
    if (this.busy++ === 0) this.onBusy(true);
    try {
      const r = await this.engine.meshStructure(hash, f.blob, f.name, cut);
      return r.tiles.map((m) => ({ x: m.cx * 16, z: m.cz * 16, solid: geometry(m.solid), translucent: geometry(m.translucent) }));
    } finally {
      if (--this.busy === 0) this.onBusy(false);
    }
  }
}

/**
 * Position a placed schematic: its box starts at block (x0, z0), turned clockwise `turns` times,
 * mirrored along x first. (Matches schematicFootprint / placeTemplate in @mcp/plan.)
 */
function orient(p: Placed, ref: SchematicRef, x0: number, y: number, z0: number) {
  const [w, , l] = ref.size;
  const t = ((ref.turns % 4) + 4) % 4;
  const [ox, oz] = [[0, 0], [l, 0], [w, l], [0, w]][t];
  p.group.position.set(x0 + ox, y, z0 + oz);
  p.group.rotation.set(0, (-t * Math.PI) / 2, 0);
  p.inner.position.set(ref.mirror ? w : 0, 0, 0);
  p.inner.scale.set(ref.mirror ? -1 : 1, 1, 1);
}

function geometry(b: MeshBuffers): THREE.BufferGeometry | null {
  if (!b.count) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(b.pos, 3));
  g.setAttribute("uvl", new THREE.BufferAttribute(b.uvl, 4));
  g.setAttribute("col", new THREE.BufferAttribute(b.col, 4, true));
  g.setIndex(new THREE.BufferAttribute(b.indices, 1));
  return g;
}

function disposeTiles(tiles: Tile[] | null) {
  for (const t of tiles ?? []) { t.solid?.dispose(); t.translucent?.dispose(); }
}
